import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { usePublicSettings } from '@/lib/site/public-settings';

const LABEL = 'Testing website';
const TEXT =
  'The content of this website is under review. This is not a public launch. It is for testing only, so please do not rely on it or use it for any public purpose.';

/** How long the strip stays on the screen each time it appears. */
export const TESTING_STRIP_MS = 3000;

/**
 * The red testing notice (D-382, D-384). Until a Super Admin confirms the go-live it appears when the website is opened or the
 * page is refreshed, and again when the visitor goes to the Home page; each time it leaves by itself after 3 seconds. Going
 * from page to page inside the website does not bring it back. It is only a notice: it never takes a click or a hover
 * (`pointer-events: none`), so the logo, Shop and the menu button under it work all the time — which is also why it cannot
 * wait for the pointer (it would "hold" whenever it appeared under a cursor that was still on the logo).
 *
 * It floats over the top of the page (`position: fixed`), so its arrival and its leaving never move the page. The pre-rendered
 * HTML carries it (`data-visible="true"`, the same as the first client render); the build fetches the go-live state, so a live
 * website has no strip at all.
 */
export function TestingStrip() {
  const { siteLive } = usePublicSettings();
  const { key, pathname } = useLocation();
  const [visible, setVisible] = useState(true);
  // The location key of the last arrival that showed the strip. Every arrival at the Home page has a new key (a click on Home
  // too, even while already there), so it shows the strip again; any other page leaves it alone. (State adjusted while
  // rendering, the pattern React recommends, not an effect that sets state.)
  const [shownKey, setShownKey] = useState(key);
  if (pathname === '/' && key !== shownKey) {
    setShownKey(key);
    setVisible(true);
  }

  useEffect(() => {
    if (!visible) return undefined;
    const timer = window.setTimeout(() => {
      setVisible(false);
    }, TESTING_STRIP_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [visible, shownKey]);

  if (siteLive === true) return null;
  return (
    <aside
      className="testing-strip"
      aria-label="Testing notice"
      data-visible={visible}
      aria-hidden={visible ? undefined : true}
    >
      <strong className="testing-strip__label">{LABEL}</strong>
      <span className="testing-strip__text">{TEXT}</span>
    </aside>
  );
}
